import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import BottomSheet from '../mobile/BottomSheet'
import MaterialIcon from '../ui/MaterialIcon'
import { KILO } from '../../constants/KILO_THEME'

type MealSubstituteSheetProps = {
  open: boolean
  mealBlockName: string
  onClose: () => void
  onSubmit: (payload: {
    dish_name: string
    calories_kcal: number
    protein_g: number
    carbs_g: number
    fat_g: number
    notes?: string
  }) => Promise<void>
  busy?: boolean
}

const MealSubstituteSheet = ({ open, mealBlockName, onClose, onSubmit, busy }: MealSubstituteSheetProps) => {
  const { t } = useTranslation()
  const [dishName, setDishName] = useState('')
  const [calories, setCalories] = useState('500')
  const [protein, setProtein] = useState('30')
  const [carbs, setCarbs] = useState('45')
  const [fats, setFats] = useState('15')
  const [notes, setNotes] = useState('')

  const handleSubmit = async () => {
    if (!dishName.trim()) return
    await onSubmit({
      dish_name: dishName.trim(),
      calories_kcal: Number(calories) || 0,
      protein_g: Number(protein) || 0,
      carbs_g: Number(carbs) || 0,
      fat_g: Number(fats) || 0,
      notes: notes.trim() || undefined,
    })
    setDishName('')
    setNotes('')
  }

  const fields = [
    { label: t('meals.substitute.calories'), value: calories, set: setCalories },
    { label: t('meals.substitute.protein'), value: protein, set: setProtein },
    { label: t('meals.substitute.carbs'), value: carbs, set: setCarbs },
    { label: t('meals.substitute.fat'), value: fats, set: setFats },
  ]

  return (
    <BottomSheet
      variant="dark"
      open={open}
      onClose={onClose}
      title={t('meals.substitute.title', { block: mealBlockName })}
    >
      <div className="flex flex-col gap-4 text-sm">
        <p className={KILO.text.muted}>{t('meals.substitute.intro')}</p>

        <label className="flex flex-col gap-1.5">
          <span className={KILO.fieldLabel}>{t('meals.substitute.dishName')}</span>
          <input
            type="text"
            value={dishName}
            onChange={(e) => setDishName(e.target.value)}
            placeholder={t('meals.substitute.dishPlaceholder')}
            className={KILO.field}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          {fields.map((field) => (
            <label key={field.label} className="flex flex-col gap-1.5">
              <span className={KILO.fieldLabel}>{field.label}</span>
              <input
                type="number"
                min={0}
                value={field.value}
                onChange={(e) => field.set(e.target.value)}
                className={KILO.field}
              />
            </label>
          ))}
        </div>

        <label className="flex flex-col gap-1.5">
          <span className={KILO.fieldLabel}>{t('meals.substitute.notes')}</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('meals.substitute.notesPlaceholder')}
            className={KILO.field}
          />
        </label>

        <button
          type="button"
          disabled={busy || !dishName.trim()}
          onClick={() => void handleSubmit()}
          className="flex h-11 items-center justify-center gap-2 rounded-xl bg-kilo-accent text-sm font-semibold text-black disabled:opacity-50"
        >
          <MaterialIcon name="check" size="sm" />
          {t('meals.substitute.saveLog')}
        </button>
      </div>
    </BottomSheet>
  )
}

export default MealSubstituteSheet
